import { commonStyles } from 'features/cart/styles';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import {
  calculateEntryChildrenPrice,
  isIncentiveOrCombo,
  isRewardCartEntry,
} from 'features/cart/utils';
import { StyleSheet, View } from 'react-native';

import Picture from 'components/picture';
import { Typography } from 'components/ui';
import { t } from 'state/intl/translations';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants';

import { CartEntryFabButton } from '../cart-actions/cart-entry-fab-button';

const ModifiersNames = ({ modifiersNames }: { modifiersNames: string }) => {
  if (!modifiersNames) {
    return null;
  }
  return (
    <View style={styles.modifiersContainer}>
      <Typography focusable style={commonStyles.cartEntryItemName}>
        {t('yourWay') + ' '}
      </Typography>
      <Typography focusable style={[commonStyles.cartEntryBody, { flexShrink: 1 }]}>
        {modifiersNames}
      </Typography>
    </View>
  );
};

const CartItem = ({
  cartEntry,
  isSingleItem = false,
  showFabButton = true,
  readOnly = false,
  allowTakeoverHeader = true,
}: {
  cartEntry: ICartEntry;
  isSingleItem?: boolean;
  showFabButton?: boolean;
  readOnly?: boolean;
  allowTakeoverHeader?: boolean;
}) => {
  const { formatPriceWithSign } = useUIContext();
  const modifiersNames = cartEntry.children.map(child => child.name).join(', ');
  const modifiersPrice = calculateEntryChildrenPrice(cartEntry);
  const price = isSingleItem ? modifiersPrice : modifiersPrice + (cartEntry.price?.amount ?? 0);
  const priceLabel = price > 0 ? formatPriceWithSign(price) : '';
  if (cartEntry.type !== MinimalCartEntryType.item) {
    return null;
  }

  const rowContent = (
    <>
      <Picture
        size="$12"
        alt={cartEntry.image.locale?.imageDescription || ''}
        image={cartEntry.image}
      />
      <View style={styles.itemDetails}>
        <Typography
          focusable
          numberOfLines={2}
          ellipsizeMode="tail"
          style={commonStyles.cartEntryItemName}
        >
          {cartEntry.name}
        </Typography>
        {priceLabel && (
          <Typography focusable style={commonStyles.cartEntryBody}>
            {priceLabel}
          </Typography>
        )}
        <ModifiersNames modifiersNames={modifiersNames} />
      </View>
      {showFabButton && <CartEntryFabButton cartEntry={cartEntry} />}
    </>
  );

  const row = <View style={[styles.item, readOnly && { borderBottomWidth: 0 }]}>{rowContent}</View>;

  return row;
};

const ComboItem = ({
  cartEntry,
  readOnly = false,
}: {
  cartEntry: ICartEntry;
  readOnly?: boolean;
}) => {
  return cartEntry.children.map(child => (
    <CartItem
      key={child.cartId ?? child.id}
      cartEntry={child}
      showFabButton={false}
      readOnly={readOnly}
      allowTakeoverHeader={false}
    />
  ));
};

const getWrappedComboBenefit = (cartEntry: ICartEntry) => {
  const benefitEntry = cartEntry.children?.[0];
  return benefitEntry?.type === MinimalCartEntryType.combo ? benefitEntry : undefined;
};

const RewardItem = ({
  cartEntry,
  readOnly = false,
}: {
  cartEntry: ICartEntry;
  readOnly?: boolean;
}) => {
  const rewardBenefit = cartEntry.children?.[0];
  if (!rewardBenefit) {
    return null;
  }
  const isItem = rewardBenefit.type === MinimalCartEntryType.item;
  if (!isItem) {
    return <ComboItem cartEntry={rewardBenefit} readOnly={readOnly} />;
  }
  return <CartItem cartEntry={rewardBenefit} isSingleItem={isItem} readOnly={readOnly} />;
};

const IncentiveItem = ({
  cartEntry,
  readOnly = false,
}: {
  cartEntry: ICartEntry;
  readOnly?: boolean;
}) => {
  const comboBenefit = getWrappedComboBenefit(cartEntry);
  if (comboBenefit) {
    return <ComboItem cartEntry={comboBenefit} readOnly={readOnly} />;
  }

  return <ComboItem cartEntry={cartEntry} readOnly={readOnly} />;
};

const CartEntry = ({
  cartEntry,
  showFabButton,
  readOnly = false,
}: {
  cartEntry: ICartEntry;
  showFabButton: boolean;
  readOnly?: boolean;
}) => {
  if (isRewardCartEntry(cartEntry)) {
    return <RewardItem cartEntry={cartEntry} readOnly={readOnly} />;
  }
  if (cartEntry.type === MinimalCartEntryType.incentive) {
    return <IncentiveItem cartEntry={cartEntry} readOnly={readOnly} />;
  }
  if (isIncentiveOrCombo(cartEntry)) {
    return <ComboItem cartEntry={cartEntry} readOnly={readOnly} />;
  }
  return (
    <CartItem
      cartEntry={cartEntry}
      isSingleItem
      showFabButton={showFabButton}
      readOnly={readOnly}
    />
  );
};

export const CartEntryBody = ({
  cartEntry,
  readOnly = false,
}: {
  cartEntry: ICartEntry;
  readOnly?: boolean;
}) => {
  return (
    <View style={{ flexDirection: 'column' }}>
      <CartEntry cartEntry={cartEntry} showFabButton={!readOnly} readOnly={readOnly} />
    </View>
  );
};

const styles = StyleSheet.create({
  modifiersContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  item: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    overflow: 'hidden',
    borderBottomWidth: 1,
    borderBottomColor: primitive.$blackOpacity10,
  },
  itemDetails: {
    flexDirection: 'column',
    gap: 4,
    flex: 1,
    minWidth: 0,
  },
});
